import { useEffect } from 'react';
import { useCall, CALL_STATE } from '../../contexts/CallContext';
import { useAuth } from '../../contexts/authContext';
import { useVcafeWebRTC } from '../../hooks/useWebRTC';

/**
 * CallMediaManager - Bridges CallContext with mediasoup SFU
 * 
 * This component handles the mediasoup connection lifecycle:
 * - Connects to mediasoup room when call is CONNECTING or IN_CALL
 * - Passes remote streams back to CallContext
 * - Handles cleanup when call ends
 */
const CallMediaManager = () => {
  const { user } = useAuth();
  const {
    callState,
    currentCall,
    localStream,
    updateRemoteStreams,
    setInCall,
  } = useCall() || {};

  // Determine if we should connect to mediasoup
  const shouldConnect = 
    (callState === CALL_STATE.CONNECTING || callState === CALL_STATE.IN_CALL) &&
    currentCall?.roomId &&
    user?.id &&
    localStream;

  // Use the mediasoup hook
  const { remoteStreams, connected, error } = useVcafeWebRTC({
    roomId: shouldConnect ? currentCall.roomId : null,
    userId: shouldConnect ? user.id : null,
    localStream: shouldConnect ? localStream : null,
  });

  // Update CallContext with remote streams when they change
  useEffect(() => {
    if (remoteStreams && updateRemoteStreams) {
      updateRemoteStreams(remoteStreams);
    }
  }, [remoteStreams, updateRemoteStreams]);

  // Transition to IN_CALL when connected
  useEffect(() => {
    if (connected && callState === CALL_STATE.CONNECTING && setInCall) {
      setInCall();
    }
  }, [connected, callState, setInCall]);

  // Log errors
  useEffect(() => {
    if (error) {
      console.error('[CallMediaManager] WebRTC error:', error);
    }
  }, [error]);

  // This component doesn't render anything - it's just a bridge
  return null;
};

export default CallMediaManager;
